import React, { useState, useEffect } from 'react';
import { api, useServer } from '../contexts/ServerContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, ChevronDown, ChevronUp, Users, LayoutDashboard, Terminal, Logs, Ban, AlertTriangle, User, Swords, MapPin, Monitor, Eye, EyeOff } from 'lucide-react';
import { steamId64ToV2, steamIdTo64 } from '../utils/helpers';
import io from 'socket.io-client';

export default function OnlineStaff() {
  const { server } = useServer();
  const { user } = useAuth();
  const [onlineStaff, setOnlineStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [showNames, setShowNames] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // socket.io
  const socketRef = React.useRef();
  React.useEffect(() => {
    setShowNames(false);
    setCollapsed(true);
    socketRef.current = io('/', {
      transports: ['websocket'],
      path: '/ws'
    });
    socketRef.current.emit('join_panel_chat', { room: `panel-${server}` });
    socketRef.current.on('panel_chat_message', (msg) => {
      setMessages((prev) => [...prev, msg]);
    });
    return () => {
      socketRef.current.emit('leave_panel_chat', { room: `panel-${server}` });
      socketRef.current.disconnect();
    };
  }, [server]);

  React.useEffect(() => {
    const el = document.getElementById('panel-chat-messages');
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const sendMessage = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    const trimmed = input.slice(0, 120);
    const msg = {
      text: trimmed,
      user: {
        displayName: user?.displayName,
        avatar: user?.avatar,
        steamId: user?.steamId,
      },
      time: new Date().toISOString(),
      server,
    };
    socketRef.current.emit('panel_chat_message', { ...msg, room: `panel-${server}` });
    setMessages((prev) => [...prev, msg]);
    setInput('');
  };

  useEffect(() => {
    if (!collapsed) {
      fetchOnlineStaff();
      const interval = setInterval(fetchOnlineStaff, 10000);
      return () => clearInterval(interval);
    }
  }, [server, location.pathname, collapsed]);

  // Пинг всегда работает, независимо от состояния панели
  useEffect(() => {
    const ping = setInterval(() => {
      api.post(`/api/admin/panel-ping?currentTab=${encodeURIComponent(location.pathname)}&server=${server}`);
    }, 10000);
    return () => clearInterval(ping);
  }, [server, location.pathname]);

  // Немедленный пинг при изменении пути
  useEffect(() => {
    api.post(`/api/admin/panel-ping?currentTab=${encodeURIComponent(location.pathname)}&server=${server}`);
  }, [location.pathname, server]);

  const fetchOnlineStaff = async () => {
    try {
      const res = await api.get(`/api/admin/online-panel-users`);
      setOnlineStaff(res.data.onlineAdmins || []);
    } catch (error) {
      console.error('Ошибка при загрузке онлайн админов панели:', error);
      setOnlineStaff([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed bottom-4 right-4 z-40">
        <div className="bg-gmod-dark border border-gmod-lighterer rounded-lg shadow-lg">
          <div className="px-4 py-2 border-b border-gmod-lighterer">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Users className="w-4 h-4 text-theme-primary mr-2" />
                <span className="text-sm font-medium text-gmod-white">На панели</span>
              </div>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-theme-primary"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const mySteamId64 = steamIdTo64(user?.steamId);

  // Функция для получения иконки вкладки по пути
  const getTabIcon = (path) => {
    switch (path) {
      case '/dashboard': return <LayoutDashboard className="w-3 h-3" />;
      case '/players': return <Users className="w-3 h-3" />;
      case '/commands': return <Terminal className="w-3 h-3" />;
      case '/punishments': return <Logs className="w-3 h-3" />;
      case '/active-bans': return <Ban className="w-3 h-3" />;
      case '/reports': return <AlertTriangle className="w-3 h-3" />;
      case '/staff': return <User className="w-3 h-3" />;
      case '/pvplogs': return <Swords className="w-3 h-3" />;
      case '/map': return <MapPin className="w-3 h-3" />;
      case '/observation': return <Monitor className="w-3 h-3" />;
      default: return <LayoutDashboard className="w-3 h-3" />;
    }
  };

  // Функция для рендера текста сообщения с картинками
  function renderChatMessageWithImages(text) {
    const imageRegex = /(?:^|\s|[@.,;:])((https?:\/\/[\S]+?\.(jpg|jpeg|png|gif)))/gi;
    const parts = [];
    let lastIndex = 0;
    let match;
    while ((match = imageRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index + (match[0].length - match[1].length)));
      }
      parts.push(
        <div key={match.index} className="my-2">
          <a href={match[1]} target="_blank" rel="noopener noreferrer">
            <img src={match[1]} alt="img" className="max-w-xs max-h-60 rounded border border-gmod-lighterer" />
          </a>
        </div>
      );
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }
    return parts;
  }

  return (
    <div className="fixed bottom-4 right-4 z-40">
      <div 
        className="bg-gmod-dark border border-gmod-lighterer rounded-lg shadow-lg max-w-full transition-all duration-300"
        style={{ width: showNames ? '520px' : '395px' }}
      >
        <div 
          className="px-4 py-2 border-b border-gmod-lighterer cursor-pointer hover:bg-gmod-lighter transition-colors"
          onClick={() => setCollapsed(!collapsed)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Users className="w-4 h-4 text-theme-primary mr-2" />
              <span className="text-sm font-medium text-gmod-white">На панели</span>
              <span className="ml-2 text-xs text-gmod-white opacity-60">({onlineStaff.length})</span>
            </div>
            <ChevronDown className={`w-4 h-4 text-gmod-white transition-transform duration-300 ${collapsed ? 'rotate-180' : ''}`} />
          </div>
        </div>
        <div 
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            collapsed ? 'max-h-0 opacity-0' : 'max-h-[520px] opacity-100'
          }`}
        >
          <div className="flex flex-row h-[500px]">
            {/* Чат слева */}
            <div className="border-r border-gmod-lighterer flex flex-col h-full w-[300px] px-2 pt-2 pb-2 min-w-[300px]">
              <div className="flex-1 overflow-y-auto flex flex-col space-y-2 mb-2 pr-1" style={{fontSize: '13px'}} id="panel-chat-messages">
                {messages.map((msg, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <img src={msg.user.avatar || '/default-avatar.png'} className="w-6 h-6 rounded-full border border-theme-primary" alt="avatar" />
                    <div>
                      <span className="font-semibold text-theme-primary mr-1">{msg.user.displayName}</span>
                      <span className="text-gmod-white">{renderChatMessageWithImages(msg.text)}</span>
                      <div className="text-xs text-gmod-white opacity-50 mt-0.5">{new Date(msg.time).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/Moscow' })}</div>
                    </div>
                  </div>
                ))}
              </div>
              <form className="flex gap-2 mt-1" onSubmit={sendMessage} autoComplete="off">
                <input
                  className="rounded bg-gmod-lighter px-2 py-1 text-gmod-white border border-gmod-lighterer focus:outline-none focus:border-theme-primary text-sm"
                  style={{ width: '100%', maxWidth: '250px' }}
                  placeholder="Сообщение..."
                  value={input}
                  onChange={e => {
                    if (e.target.value.length <= 120) setInput(e.target.value);
                  }}
                  maxLength={120}
                />
                <button type="submit" className="w-14 min-w-[44px] px-0 py-1 rounded bg-theme-primary text-gmod-white font-semibold hover:bg-theme-primary-hover transition-colors text-sm">Отпр</button>
              </form>
            </div>
            {/* Онлайн справа */}
            <div className="flex-1 flex flex-col h-full p-2 overflow-y-auto min-w-[80px]">
              <div className="flex items-center justify-center mb-2 h-8 w-full">
                <button
                  className="p-1 rounded hover:bg-gmod-lighterer transition-colors flex items-center justify-center"
                  title={showNames ? 'Скрыть имена' : 'Показать имена'}
                  onClick={() => setShowNames(v => !v)}
                  style={{ minWidth: 32, minHeight: 32 }}
                >
                  {showNames ? <EyeOff className="w-5 h-5 text-theme-primary" /> : <Eye className="w-5 h-5 text-theme-primary" />}
                </button>
              </div>
              {onlineStaff.length === 0 ? (
                <p className="text-xs text-gmod-white opacity-60 text-center">Нет админов на панели</p>
              ) : (
                <div className="space-y-2">
                  {onlineStaff.map((staff) => {
                    const isMe = steamIdTo64(staff.steamid) === mySteamId64;
                    return (
                      <div 
                        key={staff.steamid}
                        className={`flex items-center p-2 rounded cursor-pointer hover:bg-gmod-lighterer transition-colors ${isMe ? 'bg-theme-primary/20 border border-theme-primary' : 'bg-gmod-lighter'}`}
                        onClick={() => navigate(`/players/${staff.steamid}`)}
                      >
                        {staff.avatar ? (
                          <img 
                            src={staff.avatar} 
                            alt="avatar" 
                            className="w-8 h-8 rounded-full mr-2"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gmod-lighterer mr-2 flex items-center justify-center">
                            <Shield className="w-4 h-4 text-theme-primary" />
                          </div>
                        )}
                        <div
                          className={`flex-1 min-w-0 transition-all duration-300 ${showNames ? 'opacity-100 w-auto ml-1' : 'opacity-0 w-0 ml-0 pointer-events-none select-none'}`}
                          style={{ overflow: 'hidden' }}
                        >
                          <p className="text-xs font-medium text-gmod-white truncate">
                            {staff.displayName || staff.steamid} {isMe && <span className="text-theme-primary ml-1">(Вы)</span>}
                          </p>
                          <p className="text-xs text-gmod-white opacity-60 truncate">
                            {['yufuadmin', 'superadmin'].includes(staff.rank) ? 'Создатель' : staff.rank }
                          </p>
                        </div>
                        <div className="ml-2 text-theme-primary">
                          {getTabIcon(staff.currentTab || '/dashboard')}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 